/**
 * PlannerApp - 主應用程式類別
 *
 * 協調所有區段元件並管理應用程式流程：
 * 1. 預處理區段 (圖片上傳和編輯)
 * 2. 門標註區段
 * 3. 設定區段
 * 4. 視覺化區段 (結果顯示)
 */

import { PreprocessSection } from '@/components/planner/PreprocessSection';
import { DoorAnnotationSection } from '@/components/planner/DoorAnnotationSection';
import { ConfigSection } from '@/components/planner/ConfigSection';
import { VisualizationSection } from '@/components/planner/VisualizationSection';
import { StyleRenderingSection } from '@/components/planner/StyleRenderingSection';
import { SavedPlansModal, SavePlanModal } from '@/components/modals/SavedPlansModal';
import { LoginPage, UserInfo } from '@/components/auth/LoginPage';
import { showSuccessToast } from '@/components/shared/Toast';
import { authStore, logout } from '@/store/auth-store';
import { plannerStore, updateSettings, setPlannerResult } from '@/store/planner-store';
import { preprocessStore } from '@/store/preprocess-store';
import { planFurniture } from '@/api/planner-api';
import { savePlan as savePlanApi, getSavedPlan, updatePlan } from '@/api/saved-plans-api';
import { setOriginalImage, setProcessedImage } from '@/store/preprocess-store';
import { downloadBlob } from '@/utils/file';
import type { PlannerSettings, Door, Line, Rectangle } from '@/types';
import type { ReferenceLine } from '@/store/preprocess-store';
import type { SavedPlan } from '@/api/saved-plans-api';

// ========================================================================
// PlannerApp 類別
// ========================================================================

export class PlannerApp {
  private container: HTMLElement;
  private unsubscribes: (() => void)[] = [];

  // 元件
  private loginPage: LoginPage;
  private userInfo: UserInfo;
  private preprocessSection: PreprocessSection;
  private doorSection: DoorAnnotationSection;
  private configSection: ConfigSection;
  private visualizationSection: VisualizationSection;
  private styleRenderingSection: StyleRenderingSection;
  private savedPlansModal: SavedPlansModal;
  private savePlanModal: SavePlanModal;

  // 狀態
  private processedImage: string | null = null;

  constructor(container: HTMLElement) {
    this.container = container;

    // 初始化認證元件
    this.loginPage = new LoginPage({
      onLoginSuccess: () => this.showMainApp(),
    });

    this.userInfo = new UserInfo({
      onLogout: () => this.handleLogout(),
      onSavePlan: () => this.savePlanModal.open(),
      onLoadPlans: () => this.savedPlansModal.open(),
    });

    // 初始化各區段
    this.preprocessSection = new PreprocessSection({
      onComplete: (image, pixelToMeter) => this.handlePreprocessComplete(image, pixelToMeter),
    });

    this.doorSection = new DoorAnnotationSection({
      onComplete: (doors) => this.handleDoorsComplete(doors),
      onSkip: () => this.handleDoorsSkip(),
    });

    this.configSection = new ConfigSection({
      onStartPlanning: (settings) => this.startPlanning(settings),
      onQuickPlan: (settings) => this.quickPlan(settings),
    });

    this.visualizationSection = new VisualizationSection({
      onDownload: (planIndex) => this.downloadPlan(planIndex),
      onDownloadAll: () => this.downloadAllPlans(),
    });

    this.styleRenderingSection = new StyleRenderingSection({
      onGetCanvas: () => this.visualizationSection.getCanvas(),
    });

    // 初始化 Modal
    this.savedPlansModal = new SavedPlansModal({
      onSelect: (plan) => this.loadSavedPlan(plan),
    });

    this.savePlanModal = new SavePlanModal({
      onSave: (name) => this.savePlan(name, 'save'),
      onOverwrite: (name) => this.savePlan(name, 'overwrite'),
      getMode: () => plannerStore.getState().loadedPlanId ? 'loaded' : 'new',
      getCurrentPlanName: () => {
        return plannerStore.getState().currentPlanName ?? '未命名規劃';
      },
    });

    this.initialize();
  }

  // ========================================================================
  // 初始化
  // ========================================================================

  private initialize(): void {
    this.buildLayout();
    this.subscribeToStores();
    this.checkAuthStatus();
  }

  private buildLayout(): void {
    // 建立主要佈局
    this.container.innerHTML = `
      <div class="min-h-screen bg-gray-50">
        <!-- 頂部導覽 - 浮動圓角 + Liquid Glass 效果 -->
        <nav class="fixed top-4 left-4 right-4 z-50 rounded-2xl
                    bg-white/90 backdrop-blur-xl backdrop-saturate-150
                    border border-gray-200/60
                    shadow-[0_8px_32px_rgba(0,0,0,0.12),inset_0_1px_0_rgba(255,255,255,0.8)]
                    transition-all duration-300">
          <div class="container mx-auto px-4 md:px-8">
            <div class="flex justify-between h-16">
              <div class="flex items-center">
                <h1 class="text-xl font-bold text-gray-900">RaisePlan</h1>
                <span class="ml-3 text-sm text-gray-500">智能座位規劃</span>
              </div>
              <div id="userInfoContainer" class="flex items-center"></div>
            </div>
          </div>
        </nav>

        <!-- 登入頁面 -->
        <div id="loginContainer" class="hidden"></div>

        <!-- 主要內容（pt-28 為浮動導覽列留空間：top-4(16px) + h-16(64px) + 間距(32px) = 112px） -->
        <main id="mainContent" class="hidden container mx-auto px-4 md:px-8 pb-8 pt-28">
          <div id="sectionsContainer" class="space-y-8"></div>
        </main>
      </div>
    `;

    // 掛載元件
    const loginContainer = this.container.querySelector('#loginContainer') as HTMLElement;
    const userInfoContainer = this.container.querySelector('#userInfoContainer') as HTMLElement;
    const sectionsContainer = this.container.querySelector('#sectionsContainer') as HTMLElement;

    this.loginPage.mount(loginContainer);
    this.userInfo.mount(userInfoContainer);

    this.preprocessSection.mount(sectionsContainer);
    this.doorSection.mount(sectionsContainer);
    this.configSection.mount(sectionsContainer);
    this.visualizationSection.mount(sectionsContainer);
    this.styleRenderingSection.mount(sectionsContainer);
  }

  private subscribeToStores(): void {
    // 訂閱認證狀態
    const unsubAuth = authStore.subscribe((state) => {
      if (state.isAuthenticated) {
        this.showMainApp();
      } else {
        this.showLogin();
      }
    });
    this.unsubscribes.push(unsubAuth);
  }

  private checkAuthStatus(): void {
    const state = authStore.getState();
    if (state.isAuthenticated) {
      this.showMainApp();
    } else {
      this.showLogin();
    }
  }

  // ========================================================================
  // UI 狀態切換
  // ========================================================================

  private showLogin(): void {
    const loginContainer = this.container.querySelector('#loginContainer');
    const mainContent = this.container.querySelector('#mainContent');
    const userInfoContainer = this.container.querySelector('#userInfoContainer');

    loginContainer?.classList.remove('hidden');
    mainContent?.classList.add('hidden');
    userInfoContainer?.classList.add('hidden');

    // 確保 LoginPage 元件也被顯示（移除其 hidden class）
    this.loginPage.show();
  }

  private showMainApp(): void {
    const loginContainer = this.container.querySelector('#loginContainer');
    const mainContent = this.container.querySelector('#mainContent');
    const userInfoContainer = this.container.querySelector('#userInfoContainer');

    loginContainer?.classList.add('hidden');
    mainContent?.classList.remove('hidden');
    userInfoContainer?.classList.remove('hidden');

    // 確保 LoginPage 元件被隱藏
    this.loginPage.hide();
  }

  // ========================================================================
  // 事件處理
  // ========================================================================

  private handleLogout(): void {
    logout();
    // 重新載入頁面以確保所有組件狀態都被清除
    // 這比手動重置每個組件更可靠
    window.location.reload();
  }

  private handlePreprocessComplete(image: string, _pixelToMeter: number): void {
    this.processedImage = image;
    // 儲存到 store
    plannerStore.setState({ imageSrc: image });

    // 顯示門標註區段
    this.doorSection.setImage(image);
  }

  private handleDoorsComplete(doors: Door[]): void {
    plannerStore.setState({ doors });
    this.configSection.show();
    // 自動捲動到下一步驟
    this.scrollToSection(this.configSection);
  }

  private handleDoorsSkip(): void {
    plannerStore.setState({ doors: [] });
    this.configSection.show();
    // 自動捲動到下一步驟
    this.scrollToSection(this.configSection);
  }

  /**
   * 平滑捲動到指定區段
   */
  private scrollToSection(section: { getElement: () => HTMLElement }): void {
    // 延遲執行以確保 DOM 更新完成
    requestAnimationFrame(() => {
      const element = section.getElement();
      const navbarHeight = 80; // 導航欄高度 + 間距
      const elementTop = element.getBoundingClientRect().top + window.scrollY - navbarHeight;

      window.scrollTo({
        top: elementTop,
        behavior: 'smooth',
      });
    });
  }

  private async startPlanning(
    settings: PlannerSettings,
    options: { quickPlan?: boolean } = {}
  ): Promise<void> {
    if (!this.processedImage) {
      console.error('No processed image available');
      return;
    }

    updateSettings(settings);
    this.visualizationSection.show();
    const loadingMessage = options.quickPlan ? '正在快速規劃座位配置...' : '正在規劃座位配置...';
    this.visualizationSection.showLoading(loadingMessage);

    try {
      // 將 base64 圖片轉換為 File
      const response = await fetch(this.processedImage);
      const blob = await response.blob();
      const file = new File([blob], 'floorplan.png', { type: 'image/png' });

      const plannerState = plannerStore.getState();
      const preprocessState = preprocessStore.getState();

      // 準備參考線資料
      const referenceLine = preprocessState.referenceLine
        ? {
            startX: preprocessState.referenceLine.start.x,
            startY: preprocessState.referenceLine.start.y,
            endX: preprocessState.referenceLine.end.x,
            endY: preprocessState.referenceLine.end.y,
            lengthMeters: preprocessState.referenceLine.lengthMeters,
          }
        : undefined;

      const result = await planFurniture({
        image: file,
        doors: plannerState.doors,
        quickPlan: options.quickPlan,
        settings,
        referenceLine,
      });

      // 檢查 API 回應狀態
      if (result.status === 'success' || result.status === 'partial_success') {
        if (result.data?.plans && result.data.plans.length > 0) {
          // 更新 store 以觸發 Canvas 重新渲染
          setPlannerResult(result);
          // 更新視覺化區段
          this.visualizationSection.setPlans(result.data.plans, result.data.room_info);
          // 顯示風格渲染區段
          this.styleRenderingSection.show();
        } else {
          console.error('No plans returned from API');
          alert('規劃失敗：未產生任何方案');
        }
      } else {
        console.error('Planning failed:', result.errors || result.message);
        alert('規劃失敗：' + (result.errors?.join(', ') || result.message || '未知錯誤'));
      }
    } catch (error) {
      console.error('Planning error:', error);
      alert('規劃過程發生錯誤，請稍後再試');
    }
  }

  private async quickPlan(settings: PlannerSettings): Promise<void> {
    // 快速規劃：只產生一個方案（跳過尺寸組合探索）
    await this.startPlanning(settings, { quickPlan: true });
  }

  private async loadSavedPlan(plan: SavedPlan): Promise<void> {
    try {
      // 從 API 取得完整的規劃資料
      const fullPlan = await getSavedPlan(plan.id);

      // 定義預處理資料型別
      interface PreprocessData {
        referenceLine?: ReferenceLine | null;
        coverRectangles?: Rectangle[];
        blackLines?: Line[];
      }

      // 解析預處理資料（提前準備，以便在 img.onload 中使用）
      let preprocessData: PreprocessData | null = null;

      if (fullPlan.settings) {
        const settings = fullPlan.settings as Record<string, unknown>;
        const { preprocess, ...plannerSettings } = settings;

        // 將 'auto' 窗戶方向轉換為 'north'（舊版規劃資料相容處理）
        if ((plannerSettings as { windowDirection?: string }).windowDirection === 'auto') {
          (plannerSettings as { windowDirection?: string }).windowDirection = 'north';
        }

        updateSettings(plannerSettings as unknown as Partial<PlannerSettings>);

        // 從 settings.preprocess 取得預處理資料
        if (preprocess && typeof preprocess === 'object') {
          preprocessData = preprocess as PreprocessData;
        }
      }

      // 載入原始圖片
      if (fullPlan.originalImageBase64) {
        const dataUrl = `data:image/png;base64,${fullPlan.originalImageBase64}`;

        // 取得圖片尺寸
        const img = new Image();
        img.onload = () => {
          // 建立 Blob
          const byteString = atob(fullPlan.originalImageBase64!);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          const blob = new Blob([ab], { type: 'image/png' });
          const file = new File([blob], 'loaded-plan.png', { type: 'image/png' });

          // 儲存到 preprocessStore（這會重設預處理資料）
          setOriginalImage(file, dataUrl, blob, img.width, img.height);

          // 載入預處理後的圖片
          if (fullPlan.preprocessedImageBase64) {
            const processedDataUrl = `data:image/png;base64,${fullPlan.preprocessedImageBase64}`;
            setProcessedImage(processedDataUrl);
          }

          // ★ 重要：在 setOriginalImage 之後載入預處理資料（參考線、覆蓋區域、塗黑線條）
          if (preprocessData) {
            preprocessStore.setState({
              referenceLine: preprocessData.referenceLine ?? null,
              coverRectangles: preprocessData.coverRectangles ?? [],
              blackLines: preprocessData.blackLines ?? [],
            });
          }

          // 顯示預處理區段的結果
          this.preprocessSection.loadFromSavedPlan(dataUrl, fullPlan.preprocessedImageBase64
            ? `data:image/png;base64,${fullPlan.preprocessedImageBase64}`
            : dataUrl);

          // 如果有參考線，自動跳到下一步（步驟 1.5：門標註）
          if (preprocessData?.referenceLine) {
            // 觸發 onComplete 回呼，進入下一步
            const processedImage = fullPlan.preprocessedImageBase64
              ? `data:image/png;base64,${fullPlan.preprocessedImageBase64}`
              : dataUrl;
            this.handlePreprocessComplete(processedImage, preprocessData.referenceLine.lengthMeters);

            // 載入門（必須在 handlePreprocessComplete 之後）
            if (fullPlan.doors && fullPlan.doors.length > 0) {
              plannerStore.setState({ doors: fullPlan.doors });
              // 如果有門，自動跳到步驟 2（規劃參數設定）
              this.handleDoorsComplete(fullPlan.doors);
            }
          }

          // 規劃結果的載入必須在 img.onload 內，確保 preprocessStore 已有圖片
          if (fullPlan.result && fullPlan.result.length > 0) {
            // 建構符合 PlannerResponse 格式的物件
            const mockResponse = {
              status: 'success' as const,
              message: '從儲存的規劃載入',
              data: {
                room_info: {
                  area_square_meters: 0,
                  area_ping: 0,
                  pixel_to_meter_ratio: 1,
                  dimensions: { width: 0, height: 0 },
                  window_direction: 'north',
                  pillars_detected: 0,
                },
                plans: fullPlan.result,
                optimization_info: {
                  method: 'loaded',
                  phase1_time_seconds: 0,
                  phase2_time_seconds: 0,
                  total_time_seconds: 0,
                  solver_status: 'loaded',
                },
              },
              timestamp: new Date().toISOString(),
            };
            setPlannerResult(mockResponse);

            // 顯示視覺化區段
            this.visualizationSection.setPlans(fullPlan.result);
            this.visualizationSection.show();
            // 顯示風格渲染區段
            this.styleRenderingSection.show();

            // 載入風格渲染結果（如果有的話）
            if (fullPlan.styleRenderedImageBase64) {
              this.styleRenderingSection.loadRenderedImage(fullPlan.styleRenderedImageBase64);
            }
          }
        };
        img.src = dataUrl;
      }

      // 設定使用者資訊顯示當前規劃名稱
      this.userInfo.setCurrentPlanName(fullPlan.name);

      // 設定當前載入的規劃 ID 和名稱
      const { setLoadedPlanId, setCurrentPlanName } = await import('@/store/planner-store');
      setLoadedPlanId(plan.id);
      setCurrentPlanName(fullPlan.name);

      showSuccessToast(`已載入規劃：${fullPlan.name}`);
    } catch (error) {
      console.error('Load saved plan error:', error);
      alert('載入規劃失敗，請稍後再試');
    }
  }

  private async savePlan(name: string, action: 'save' | 'overwrite' = 'save'): Promise<void> {
    const plannerState = plannerStore.getState();
    const preprocessState = preprocessStore.getState();

    // 檢查是否有原始圖片
    if (!preprocessState.originalDataUrl) {
      alert('請先上傳平面圖');
      return;
    }

    try {
      // 從 Data URL 提取 base64 字串（移除 "data:image/xxx;base64," 前綴）
      const extractBase64 = (dataUrl: string): string => {
        const base64Index = dataUrl.indexOf('base64,');
        return base64Index !== -1 ? dataUrl.substring(base64Index + 7) : dataUrl;
      };

      const originalBase64 = extractBase64(preprocessState.originalDataUrl);
      // 使用處理後的圖片，如果沒有則用原始圖片
      const preprocessedBase64 = preprocessState.processedDataUrl
        ? extractBase64(preprocessState.processedDataUrl)
        : originalBase64;

      // 取得規劃結果（如果有的話），沒有則用空陣列
      const plans = plannerState.lastResponse?.data?.plans ?? [];

      // 整合規劃設定和預處理資料
      const settingsWithPreprocess = {
        ...plannerState.settings,
        // 預處理資料
        preprocess: {
          referenceLine: preprocessState.referenceLine,
          coverRectangles: preprocessState.coverRectangles,
          blackLines: preprocessState.blackLines,
        },
      };

      // 取得風格渲染結果（如果有的話）
      const styleRenderedBase64 = await this.styleRenderingSection.getRenderedImageBase64();

      // 準備請求資料
      const requestData = {
        name: name || undefined,
        original_image_base64: originalBase64,
        preprocessed_image_base64: preprocessedBase64,
        doors: plannerState.doors,
        settings: settingsWithPreprocess as unknown as Record<string, unknown>,
        result: plans,
        style_rendered_image_base64: styleRenderedBase64 || undefined,
      };

      // 根據 action 呼叫不同的 API
      let response;
      if (action === 'overwrite' && plannerState.loadedPlanId) {
        // 覆蓋現有規劃
        response = await updatePlan(plannerState.loadedPlanId, requestData);
      } else {
        // 儲存新規劃或另存
        response = await savePlanApi(requestData);
      }

      if (response.success) {
        // 更新 loadedPlanId 和 currentPlanName
        const { setLoadedPlanId, setCurrentPlanName } = await import('@/store/planner-store');
        if (action === 'save') {
          // 新規劃或另存：更新 loadedPlanId
          setLoadedPlanId(response.plan_id);
        }
        // 覆蓋時 loadedPlanId 保持不變

        // 兩種情況都要更新 currentPlanName
        setCurrentPlanName(name);

        // 更新使用者資訊顯示
        this.userInfo.setCurrentPlanName(name);

        // 顯示成功訊息
        const actionText = action === 'overwrite' ? '已覆蓋更新' : '已儲存';
        showSuccessToast(`規劃${actionText}！ (ID: ${response.plan_id})`);
      } else {
        alert('儲存失敗：' + (response.error || '未知錯誤'));
      }
    } catch (error) {
      console.error('Save plan error:', error);
      alert('儲存過程發生錯誤，請稍後再試');
    }
  }

  /**
   * 下載單一方案的規劃圖（PNG 圖片）
   */
  private async downloadPlan(_planIndex: number): Promise<void> {
    try {
      // 取得當前 Canvas 截圖
      const blob = await this.visualizationSection.getCanvasBlob();

      if (!blob) {
        alert('無法取得規劃圖');
        return;
      }

      // 產生檔名
      const currentIndex = this.visualizationSection.getCurrentPlanIndex();
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
      const filename = `RaisePlan_${currentIndex + 1}_${timestamp}.png`;

      downloadBlob(blob, filename);
      showSuccessToast(`已下載方案 ${currentIndex + 1} 的規劃圖`);
    } catch (error) {
      console.error('Download plan error:', error);
      alert('下載失敗，請稍後再試');
    }
  }

  /**
   * 下載全部方案的規劃圖
   * 目前實作：下載當前顯示的方案圖片
   */
  private async downloadAllPlans(): Promise<void> {
    const totalPlans = this.visualizationSection.getTotalPlans();

    if (totalPlans === 0) {
      alert('沒有可下載的方案');
      return;
    }

    // 目前只下載當前方案的圖片
    // TODO: 未來可擴展為下載所有方案的截圖並打包成 ZIP
    await this.downloadPlan(this.visualizationSection.getCurrentPlanIndex());
  }

  // ========================================================================
  // 清理
  // ========================================================================

  destroy(): void {
    this.unsubscribes.forEach((unsub) => unsub());
    this.preprocessSection.destroy();
    this.doorSection.destroy();
    this.configSection.destroy();
    this.visualizationSection.destroy();
    this.styleRenderingSection.destroy();
    this.savedPlansModal.destroy();
    this.savePlanModal.destroy();
  }
}
